import React, { useState, useEffect, useRef, useCallback } from 'react'
import Radio from '/components/elements/form/Radio'
import styles from './styles.module.scss'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import isEmpty from 'lodash-es/isEmpty'
import { useSearchContext } from '/providers/SearchContext'
import axios from '/components/configs/axios'
import { useRouter } from 'next/router'
import Grid from '/components/elements/Grid'
import classNames from 'classnames'
import CustomField from '../../../form/CustomField'
import IconLocation from '/public/icons/icon-location.svg'

const Locations = ({ form: { setFieldValue } }) => {
  // Get necessary context and router
  const { search, setSearch } = useSearchContext()
  const router = useRouter()
  const { isMobile, isTablet, isSmallMobile } = useResponsive()

  // Component state
  const [checked, setChecked] = useState(search.location || [])
  const [locations, setLocations] = useState(null)
  const [show, setShow] = useState(false)
  const [locationMounted, setLocationMounted] = useState(false)
  const [displayText, setDisplayText] = useState('Locations')

  // Refs
  const wrapperRef = useRef(null)
  const transitionTimerRef = useRef(null)

  // Helper function to replace hyphens with spaces
  const replaceInArray = useCallback((str) => {
    return str.replace(/-/g, ' ')
  }, [])

  // Load locations data based on route
  const loadLocations = useCallback(() => {
    const isRealEstate = router.pathname.includes('real-estate')
    const endpoint = isRealEstate
      ? 'real-estate/neighbourhood'
      : 'listings/neighbourhood'

    axios
      .get(endpoint)
      .then((res) => {
        setLocations(res.data)
      })
      .catch((err) => console.error(err))
  }, [router.pathname])

  // Update label and checked state from search context
  const updateLabelFromSearch = useCallback(() => {
    if (!isEmpty(search.location)) {
      setChecked(search.location)
      const formattedLabel = search.location.map(replaceInArray).join(', ')
      setDisplayText(formattedLabel)
    } else {
      setDisplayText('Locations')
    }
  }, [search.location, replaceInArray])

  // Handle checkbox change with inline submission
  const handleChange = useCallback(
    (e) => {
      setChecked((prevChecked) => {
        let updatedList = [...prevChecked]

        if (e.target.checked) {
          updatedList = [...prevChecked, e.target.value]
        } else {
          updatedList.splice(prevChecked.indexOf(e.target.value), 1)
        }

        // Update form and context directly here instead of through useEffect
        setFieldValue('location', updatedList)
        setSearch((prevSearch) => ({ ...prevSearch, location: updatedList }))

        // Update label and display text
        if (!isEmpty(updatedList)) {
          const formattedText = updatedList.map(replaceInArray).join(', ')
          setDisplayText(formattedText)
        } else {
          setDisplayText('Locations')
        }

        return updatedList
      })
    },
    [setFieldValue, setSearch, replaceInArray]
  )

  // Toggle dropdown show/hide with proper transition handling
  const toggleShow = useCallback(() => {
    if (!show) {
      // Opening dropdown
      setShow(true)
      setLocationMounted(true)
    } else {
      // Closing dropdown
      setShow(false)

      // Wait for transition to finish before unmounting content
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current)
      }

      const transitionDuration = 300
      transitionTimerRef.current = setTimeout(() => {
        setLocationMounted(false)
      }, transitionDuration)
    }
  }, [show])

  // Handle clicks outside the component
  const handleClickOutside = useCallback(
    (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        show
      ) {
        toggleShow()
      }
    },
    [show, toggleShow]
  )

  // Initial setup - equivalent to componentDidMount
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    loadLocations()
    updateLabelFromSearch()

    // Cleanup - equivalent to componentWillUnmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current)
      }
    }
  }, [handleClickOutside, loadLocations, updateLabelFromSearch])

  // Watch for search context changes - equivalent to componentDidUpdate
  useEffect(() => {
    updateLabelFromSearch()
  }, [search, updateLabelFromSearch])

  // Don't render until data is loaded
  if (!locations) {
    return null
  }

  return (
    <div ref={wrapperRef}>
      <CustomField
        aria-label="Select Location"
        value={displayText}
        onClick={toggleShow}
        width={isTablet ? 6 : 9}
        prependIcon={<IconLocation width={26} height={26} />}
        isActive={!isEmpty(checked)}
        variant={[isMobile && 'full-width']}
      />

      <div className={classNames(styles.field, show && styles.isShown)}>
        {locationMounted && locations && (
          <div className={styles.location}>
            {locations.map(
              (item, index) =>
                !isEmpty(item.options) && (
                  <div className={styles.location__column} key={index}>
                    <div className={styles.location__title}>{item.title}</div>
                    <Grid
                      variant={[
                        isSmallMobile
                          ? 'grid-1'
                          : isMobile
                          ? 'grid-2'
                          : isTablet
                          ? 'grid-2'
                          : item.options.length > 5
                          ? 'rows-6'
                          : 'rows-5',
                        'rows-2xs'
                      ]}
                    >
                      {item.options.map((radio, number) => (
                        <Radio
                          key={`${index}-${number}`}
                          name="location"
                          variant={['is-close', 'small']}
                          checkbox
                          value={radio.value}
                          label={radio.label}
                          checked={checked?.includes(radio.value)}
                          onChange={handleChange}
                        />
                      ))}
                    </Grid>
                  </div>
                )
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Locations
